/** @jsx jsx */

/** @jsxFrag React.Fragment */

/**
 * NOTE:
 * This file requires a schema.json file to be generated in this folder.
 *
 * If your linter is failing on this file, make sure the apollo package is installed,
 * and then run `yarn graphql:schema` inside of static/bundles/xdp/components/shared/recommendations
 *
 * That will generate the required file and the linter should work.
 */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, Tab, TabContext, TabPanel, Tabs, Typography } from '@coursera/cds-core';

import withPremiumProductCollections from 'bundles/browse/components/Domain/withPremiumProductCollections';
import type { PremiumProductProps } from 'bundles/browse/components/Domain/withPremiumProductCollections';
import { ProductVariantNames } from 'bundles/browse/constants';
import DegreeProductsSlider from 'bundles/enterprise-legacy-xdp/components/products-slider/DegreeProductsSlider';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/premium-certificate';

export type Props = {
  // Prop is used in below HOC
  domainIds?: string[];
  slug?: string | null;
  productVariant?: string;
  universityCertificateListFromPremiumProductsCollection?: PremiumProductProps[];
  mastertrackListFromPremiumProductsCollection?: PremiumProductProps[];
  title?: string;
};

const styles = {
  mainTitle: css`
    font-size: 24px;
    text-align: left;
    padding: 48px 0 32px;
  `,
  certificateRecommendationsWrapper: css`
    margin: 12px auto 0;
    max-width: 1466px;
    width: 100%;
  `,
  gridItemWrap: (title: string | undefined) => css`
    ${!title && 'padding: 0 48px'};
    position: relative;
    width: 100%;

    @media screen and (max-width: 608px) {
      padding: 0 24px;
    }
  `,
  gridItemInsideWrap: () => css`
    border-bottom: solid 1px var(--cds-color-grey-200);
    padding: 0 0 64px;
    width: 100%;
  `,
  sliderTitleWrap: css`
    h3 {
      margin: 24px 0 32px;
    }

    .rc-ProductsSlider .slick-initialized .slick-track {
      text-align: left;
    }
  `,
  sliderWrapper: css`
    position: relative;

    .slick-list {
      overflow: hidden;
    }

    .arrow-button-prev {
      margin-left: -6px;
    }

    .arrow-button-next {
      margin-right: -6px;
    }

    .rc-BrowseDegreeCard .card-icon {
      overflow: hidden;
      position: relative;
    }

    .rc-BrowseDegreeCard .card-icon .degree-icon {
      object-fit: cover;
    }

    .carousel-dots {
      /* This !important is not great, but there's no easy way to override since the
    slick slider styles set display: block inline. */
      display: block !important;
      cursor: pointer;
      position: absolute;
      width: 100%;
      padding: 0;
      list-style: none;
      text-align: center;

      li {
        display: inline-block;
        width: 20px;
        height: 20px;

        button {
          position: relative;
          visibility: hidden;
          width: 100%;

          &::before {
            color: #9b9b9b;
            content: '○';
            visibility: visible;
            position: absolute;
            left: 2px;
            display: block;
            font-size: 16px;
            width: 14px;
          }
        }

        &.slick-active {
          button::before {
            content: '●';
          }
        }
      }
    }
  `,
};

export const CertificateTabbedRecommendations = (props: Props) => {
  const {
    slug,
    productVariant,
    universityCertificateListFromPremiumProductsCollection: certificateList,
    mastertrackListFromPremiumProductsCollection: mastertrackList,
    title,
  } = props;
  const defaultVisibleSlider = productVariant ?? ProductVariantNames.Mastertrack;

  const [tabValue, setTabValue] = React.useState<string>(defaultVisibleSlider);
  const handleTabChange = (value: string) => {
    setTabValue(value);
  };

  if (!slug) return null;

  const filteredMasterTracks = mastertrackList?.filter((mastertrack) => mastertrack.slug !== slug);
  const filteredCertificates = certificateList?.filter((certificate) => certificate.slug !== slug);

  const cardFooterLabel = _t('Learn from Top Universities');

  /**
   * We're showing and hiding some elements based on special conditions.
   * Here are the cases:
   *
   * 1. Both MasterTracks and Certificates have items: render both sliders with tabs.
   * 2. Either MTs or Certs exist, but not both: render only one slider, with no tabs.
   *
   * The display logic is a bit messy, but the above cases proved to be very particular.
   */
  const masterTracksHaveItems = filteredMasterTracks && filteredMasterTracks.length > 0;
  const certsHaveItems = filteredCertificates && filteredCertificates.length > 0;

  const masterTracksAndCertsHaveItems = masterTracksHaveItems && certsHaveItems;

  const masterTrackTab = (
    <Tab
      key={`${ProductVariantNames.Mastertrack}-tab`}
      className={ProductVariantNames.Mastertrack}
      label={_t('MasterTrack® Certificates')}
      value={ProductVariantNames.Mastertrack}
    />
  );

  const certificatesTab = (
    <Tab
      key={`${ProductVariantNames.UniversityCertificate}-tab`}
      className={ProductVariantNames.UniversityCertificate}
      label={_t('University Certificates')}
      value={ProductVariantNames.UniversityCertificate}
    />
  );

  const pageTitle = title ? (
    <Typography variant="h2semibold" css={styles.mainTitle}>
      {title}
    </Typography>
  ) : null;

  const masterTrackPanel = masterTracksHaveItems && (
    <div css={styles.sliderTitleWrap}>
      <Typography variant="h2semibold" component="h3">
        {_t('Earn an university-issued credential and credit towards a degree')}
      </Typography>
      <div css={styles.sliderWrapper}>
        <DegreeProductsSlider
          fullWidth={true}
          products={filteredMasterTracks.slice(0, 6)}
          forceOpenInNewTab={true}
          footerLabel={cardFooterLabel}
        />
      </div>
    </div>
  );

  const certificatesPanel = certsHaveItems && (
    <div css={styles.sliderTitleWrap}>
      <Typography variant="h2semibold" component="h3">
        {_t('Earn an affordable, university-issued credential from top schools')}
      </Typography>
      <div css={styles.sliderWrapper}>
        <DegreeProductsSlider
          fullWidth={true}
          products={filteredCertificates.slice(0, 6)}
          forceOpenInNewTab={true}
          footerLabel={cardFooterLabel}
        />
      </div>
    </div>
  );

  const allTabs = () => {
    if (productVariant === ProductVariantNames.UniversityCertificate) {
      return [certificatesTab, masterTrackTab];
    } else {
      return [masterTrackTab, certificatesTab];
    }
  };

  return (
    <TrackedDiv
      trackingName="certificate_recommendations"
      trackClicks={false}
      requireFullyVisible
      withVisibilityTracking
    >
      <Grid container css={styles.certificateRecommendationsWrapper} id="recommendations">
        <Grid item css={styles.gridItemWrap(title)}>
          {(masterTrackPanel || certificatesPanel) && (
            <div css={styles.gridItemInsideWrap}>
              {masterTracksAndCertsHaveItems ? (
                <TabContext value={tabValue}>
                  {pageTitle}
                  <Tabs aria-label={_t('Related certificate program recommendations')} onChange={handleTabChange}>
                    {allTabs().map((tabItem) => tabItem)}
                  </Tabs>

                  <TabPanel value={ProductVariantNames.Mastertrack}>{masterTrackPanel}</TabPanel>
                  <TabPanel value={ProductVariantNames.UniversityCertificate}>{certificatesPanel}</TabPanel>
                </TabContext>
              ) : (
                <>
                  {pageTitle}
                  {masterTrackPanel}
                  {certificatesPanel}
                </>
              )}
            </div>
          )}
        </Grid>
      </Grid>
    </TrackedDiv>
  );
};
export default withPremiumProductCollections(({ domainIds }: Props) => {
  return {
    domains: domainIds,
    productVariants: [ProductVariantNames.Mastertrack, ProductVariantNames.UniversityCertificate],
  };
})(CertificateTabbedRecommendations);
